/**
 * A03 — Balance Générale: FE form ↔ BE situation_balance_generale column mapping.
 * Writable columns only (excludes id, balance_ref, created_at, updated_at).
 */

export const BALANCE_WRITABLE_FIELDS = [
  'exercice_fiscal',
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'type_balance',
  'statut_balance',
  'numero_compte',
  'libelle_compte',
  'compte_classe',
  'solde_debiteur_debut',
  'solde_crediteur_debut',
  'total_mouvements_debit',
  'total_mouvements_credit',
  'solde_debiteur_fin',
  'solde_crediteur_fin',
  'solde_net',
  'sens_solde',
  'nombre_ecritures',
  'derniere_ecriture_date',
  'compte_actif',
  'compte_passif',
  'compte_charge',
  'compte_produit',
  'compte_lettrable',
  'compte_lettre_pourcent',
  'compte_actif_balance',
  'anomalies_detectees',
  'commentaires_compte',
  'notes_verification',
  'rapprochements_effectues',
  'rapprochements_details',
  'validation_comptable',
  'validation_date',
  'conformite_pcn',
  'numero_pcn_standard',
  'notes',
  'metadata',
  'created_by',
];

const DATE_FIELDS = new Set([
  'periode_debut',
  'periode_fin',
  'date_etablissement',
  'derniere_ecriture_date',
  'validation_date',
]);

const DECIMAL_FIELDS = new Set([
  'solde_debiteur_debut',
  'solde_crediteur_debut',
  'total_mouvements_debit',
  'total_mouvements_credit',
  'solde_debiteur_fin',
  'solde_crediteur_fin',
  'solde_net',
  'compte_lettre_pourcent',
]);

const INTEGER_FIELDS = new Set(['nombre_ecritures']);

const BOOLEAN_FIELDS = new Set([
  'compte_actif',
  'compte_passif',
  'compte_charge',
  'compte_produit',
  'compte_lettrable',
  'compte_actif_balance',
  'rapprochements_effectues',
  'validation_comptable',
  'conformite_pcn',
]);

const BOOLEAN_DEFAULTS = Object.freeze({
  compte_actif: false,
  compte_passif: false,
  compte_charge: false,
  compte_produit: false,
  compte_lettrable: false,
  compte_actif_balance: true,
  rapprochements_effectues: false,
  validation_comptable: false,
  conformite_pcn: true,
});

export const EMPTY_BALANCE_FORM = Object.freeze({
  ...BALANCE_WRITABLE_FIELDS.reduce((acc, field) => {
    acc[field] = BOOLEAN_FIELDS.has(field) ? BOOLEAN_DEFAULTS[field] : '';
    return acc;
  }, {}),
});

const normalizeDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  return String(value);
};

const toFormScalar = (field, value) => {
  if (value == null) {
    return BOOLEAN_FIELDS.has(field) ? BOOLEAN_DEFAULTS[field] : '';
  }
  if (DATE_FIELDS.has(field)) return normalizeDate(value);
  if (BOOLEAN_FIELDS.has(field)) return value === 1 || value === true;
  return value;
};

/** Map API row → form state (all writable columns). */
export const recordToFormData = (record = {}) => {
  const form = { ...EMPTY_BALANCE_FORM };
  BALANCE_WRITABLE_FIELDS.forEach((field) => {
    if (record[field] !== undefined && record[field] !== null) {
      form[field] = toFormScalar(field, record[field]);
    }
  });
  return form;
};

/** Map form state → API payload (typed for situation_balance_generale). */
export const buildBalancePayload = (formData = {}) => {
  const payload = {};

  BALANCE_WRITABLE_FIELDS.forEach((field) => {
    const raw = formData[field];

    if (DECIMAL_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseFloat(raw) || 0;
      return;
    }

    if (INTEGER_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? 0 : parseInt(raw, 10) || 0;
      return;
    }

    if (BOOLEAN_FIELDS.has(field)) {
      payload[field] = raw === true || raw === 1 || raw === '1';
      return;
    }

    if (DATE_FIELDS.has(field)) {
      payload[field] = raw === '' || raw == null ? null : normalizeDate(raw);
      return;
    }

    payload[field] = raw === '' || raw == null ? null : raw;
  });

  return payload;
};
